<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Aceleres — linha da lista com duas composições: desktop densa, como Gmail,
// e mobile empilhada para preservar leitura e áreas de toque.
const props = defineProps({
  thread: { type: Object, required: true },
  active: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
  showAccount: { type: Boolean, default: false },
  availableLabels: { type: Array, default: () => [] },
});

const emit = defineEmits(['open', 'toggle-select', 'toggle-star']);
const { t, locale } = useI18n();

const senderName = computed(
  () =>
    props.thread.sender?.name ||
    props.thread.sender?.email ||
    t('INBOX.EMAIL.UNKNOWN_SENDER')
);

const starLabel = computed(() =>
  props.thread.starred ? t('INBOX.EMAIL.UNSTAR') : t('INBOX.EMAIL.STAR')
);
const labelColor = name =>
  props.availableLabels.find(label => label.title === name)?.color || '#16834b';

const formattedDate = computed(() => {
  const timestamp = Number(props.thread.last_email_at || 0);
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const language = locale.value?.replace('_', '-') || 'pt-BR';
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(language, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString(language, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
});

const fullDate = computed(() => {
  const timestamp = Number(props.thread.last_email_at || 0);
  return timestamp
    ? new Date(timestamp * 1000).toLocaleString(
        locale.value?.replace('_', '-') || 'pt-BR'
      )
    : '';
});

const rowClass = computed(() => {
  if (props.active) return 'bg-[#e6f4ea]';
  if (props.selected) return 'bg-[#f0f7f2]';
  return props.thread.unread
    ? 'bg-white hover:bg-[#f5faf6]'
    : 'bg-[#fbfcfb] hover:bg-[#f5faf6]';
});
</script>

<template>
  <div
    class="border-b border-[#e8eaed] text-left transition-colors duration-150"
    :class="rowClass"
  >
    <div
      class="hidden min-h-[3.25rem] items-center gap-x-3 px-3 text-[13px] md:grid md:grid-cols-[1.5rem_1.5rem_minmax(11rem,0.26fr)_minmax(0,1fr)_4.5rem] md:px-4"
    >
      <input
        v-if="!thread.draft_id"
        type="checkbox"
        class="size-4 cursor-pointer accent-[#15603e]"
        :checked="selected"
        :aria-label="t('INBOX.EMAIL.SELECT_THREAD')"
        @click.stop
        @change="emit('toggle-select', thread.id)"
      />
      <span v-else aria-hidden="true" />
      <button
        v-if="!thread.draft_id"
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#9aa0a6] hover:bg-[#e8eaed]"
        :class="thread.starred ? '!text-[#d99a00]' : ''"
        :aria-label="starLabel"
        :aria-pressed="thread.starred"
        @click.stop="emit('toggle-star', thread)"
      >
        <svg
          class="size-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polygon
            points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
            :class="thread.starred ? 'fill-current' : ''"
          />
        </svg>
      </button>
      <span v-else aria-hidden="true" />
      <span
        v-if="thread.unread"
        role="img"
        class="sr-only"
        :aria-label="t('INBOX.EMAIL.UNREAD')"
      />
      <button
        type="button"
        data-testid="email-list-item-open"
        class="col-span-2 grid min-w-0 grid-cols-[minmax(11rem,0.26fr)_minmax(0,1fr)] items-center gap-x-3 text-left"
        @click="emit('open', thread)"
      >
        <span class="flex min-w-0 items-center gap-1.5">
          <span
            v-if="thread.unread"
            class="size-2 shrink-0 rounded-full bg-[#16834b]"
            aria-hidden="true"
          />
          <span
            class="truncate"
            :class="
              thread.unread
                ? 'font-bold text-[#202124]'
                : 'font-normal text-[#5f6368]'
            "
          >
            {{ senderName }}
          </span>
          <span
            v-if="thread.message_count > 1"
            class="shrink-0 text-xs text-[#80868b]"
            >{{ thread.message_count }}</span
          >
          <span
            v-if="showAccount && thread.inbox_email"
            class="hidden min-w-0 truncate text-[11px] text-[#80868b] 2xl:inline"
            >{{ thread.inbox_email }}</span
          >
        </span>
        <span class="flex min-w-0 items-baseline gap-1.5">
          <span
            v-for="label in thread.labels"
            :key="label"
            class="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-white"
            :style="{ backgroundColor: labelColor(label) }"
            >{{ label }}</span
          >
          <span
            class="min-w-0 truncate"
            :class="
              thread.unread
                ? 'font-bold text-[#202124]'
                : 'font-normal text-[#5f6368]'
            "
            >{{ thread.subject || t('INBOX.EMAIL.NO_SUBJECT') }}</span
          >
          <span v-if="thread.snippet" class="min-w-0 truncate text-[#80868b]">
            — {{ thread.snippet }}
          </span>
          <span
            v-if="thread.has_attachments"
            class="i-lucide-paperclip size-3.5 shrink-0 text-[#80868b]"
            aria-hidden="true"
          />
        </span>
      </button>
      <span
        class="truncate text-right text-xs"
        :class="thread.unread ? 'font-bold text-[#202124]' : 'text-[#5f6368]'"
        :title="fullDate"
        >{{ formattedDate }}</span
      >
    </div>

    <div
      class="grid min-h-[4.5rem] grid-cols-[1.5rem_1.75rem_minmax(0,1fr)] gap-x-2 px-3 py-2 md:hidden"
    >
      <input
        v-if="!thread.draft_id"
        type="checkbox"
        class="mt-1 size-4 cursor-pointer accent-[#15603e]"
        :checked="selected"
        :aria-label="t('INBOX.EMAIL.SELECT_THREAD')"
        @click.stop
        @change="emit('toggle-select', thread.id)"
      />
      <span v-else aria-hidden="true" />
      <button
        v-if="!thread.draft_id"
        type="button"
        class="mt-[-0.2rem] inline-flex size-9 items-center justify-center rounded-full text-[#9aa0a6] hover:bg-[#e8eaed]"
        :class="thread.starred ? '!text-[#d99a00]' : ''"
        :aria-label="starLabel"
        :aria-pressed="thread.starred"
        @click.stop="emit('toggle-star', thread)"
      >
        <svg
          class="size-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polygon
            points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
            :class="thread.starred ? 'fill-current' : ''"
          />
        </svg>
      </button>
      <span v-else aria-hidden="true" />
      <button
        type="button"
        data-testid="email-list-item-open"
        class="min-w-0 text-left"
        @click="emit('open', thread)"
      >
        <span class="flex items-center justify-between gap-2">
          <span
            v-if="thread.unread"
            class="size-2 shrink-0 rounded-full bg-[#16834b]"
            aria-hidden="true"
          />
          <span
            class="min-w-0 truncate text-[13px]"
            :class="
              thread.unread
                ? 'font-bold text-[#202124]'
                : 'font-medium text-[#5f6368]'
            "
            >{{ senderName }}</span
          >
          <span
            v-if="thread.message_count > 1"
            class="shrink-0 text-xs text-[#5f6368]"
            >{{ thread.message_count }}</span
          >
          <span
            class="shrink-0 text-[11px]"
            :class="
              thread.unread ? 'font-bold text-[#202124]' : 'text-[#5f6368]'
            "
            >{{ formattedDate }}</span
          >
        </span>
        <span class="mt-1 flex min-w-0 items-baseline gap-1">
          <span
            v-for="label in thread.labels"
            :key="label"
            class="shrink-0 rounded px-1 py-0.5 text-[10px] text-white"
            :style="{ backgroundColor: labelColor(label) }"
            >{{ label }}</span
          >
          <span
            class="min-w-0 shrink-0 truncate text-[13px]"
            :class="
              thread.unread ? 'font-semibold text-[#202124]' : 'text-[#5f6368]'
            "
            >{{ thread.subject || t('INBOX.EMAIL.NO_SUBJECT') }}</span
          >
          <span
            v-if="thread.snippet"
            class="min-w-0 truncate text-xs text-[#80868b]"
            >— {{ thread.snippet }}</span
          >
        </span>
        <span
          v-if="showAccount && thread.inbox_email"
          class="mt-1 block truncate text-[11px] text-[#80868b]"
          >{{ thread.inbox_email }}</span
        >
      </button>
    </div>
  </div>
</template>
